/**
 * 화면 3 · 호출됨 (ReadyScreen) — 디자인 원본 Pantalla 3.
 *
 * @see #781 / admin#2111 설계 §6.1 / §8-3 (GPS 실패 구분)
 *
 * GPS 취득과 API 호출은 페이지가 담당하고 이 화면은 결과 표시만 한다 —
 * navigator.geolocation 을 화면에 두면 렌더 테스트마다 브라우저 API 스텁이 필요해진다.
 */
import { Bell, CheckCircle2, MapPin, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import type { WaitlistPartyData } from '@/lib/api/waitlist';
import { WaitlistDeadlineCountdown } from './WaitlistDeadlineCountdown';
import { WaitlistRow, WaitlistSegments, WaitlistWarning } from './WaitlistParts';
import type { WaitlistStrings } from './strings';

/**
 * 도착 확인 실패 사유.
 * - `TOO_FAR`: 반경 밖 — "가까이 오세요" (재시도 유효)
 * - `GPS_UNAVAILABLE`: 좌표 취득 불가/미제공 — "호스트에게 문의" (설계 §8-3, 줄에서 밀어내지 않음)
 * - `GENERIC`: 그 외 실패 — 일반 재시도 안내
 */
export type WaitlistArriveError = 'TOO_FAR' | 'GPS_UNAVAILABLE' | 'GENERIC';

interface WaitlistReadyScreenProps {
  t: WaitlistStrings;
  party: WaitlistPartyData;
  /** 매장 GPS 검증 설정 — 안내 문구 노출 여부. */
  gpsEnabled: boolean;
  /** 위치 취득/도착 확인 진행 중. */
  arriving: boolean;
  /** 줄 이탈 요청 진행 중. */
  leaving?: boolean;
  arriveError: WaitlistArriveError | null;
  onArrive: () => void;
  onLeave: () => void;
  /** 호출 만료 시 서버 상태를 즉시 다시 조회한다. */
  onCallExpired?: () => void;
  /**
   * 픽업 줄서기 게이트 매장(admin#2172/menu#804) — `pickup_requires_waitlist` && `store_slug`
   * 둘 다 있을 때만 "앱에서 주문하기" 버튼을 노출한다. optional: 구서버 호환.
   */
  storeMeta?: { pickup_requires_waitlist?: boolean; store_slug?: string };
  /** "앱에서 주문하기" 버튼 클릭 — `/restaurant/{slug}` 픽업 모드 이동은 호출자가 담당. */
  onOrderInApp?: () => void;
}

export function WaitlistReadyScreen({
  t,
  party,
  gpsEnabled,
  arriving,
  leaving = false,
  arriveError,
  onArrive,
  onLeave,
  onCallExpired,
  storeMeta,
  onOrderInApp,
}: WaitlistReadyScreenProps) {
  const pk = party.queue_type === 'pickup';
  const arrivalConfirmed = party.arrived_at != null;
  const arrivalLabel = pk ? t.pkArrived : t.yaLlegue;
  const holdLabel = pk ? t.pkGuardamos : t.guardamos;
  const showOrderInApp = pk && Boolean(storeMeta?.pickup_requires_waitlist && storeMeta.store_slug);
  const errorMessage =
    arriveError === 'TOO_FAR'
      ? t.gpsFar
      : arriveError === 'GPS_UNAVAILABLE'
        ? t.gpsUnavailable
        : arriveError === 'GENERIC'
          ? t.errorGenerico
          : null;

  return (
    <div className="flex flex-1 flex-col overflow-y-auto">
      <div className="bg-primary px-6 pb-10 pt-12 text-center text-primary-foreground">
        <div className="mx-auto mb-5 grid size-[76px] place-items-center rounded-full bg-white/20">
          <Bell className="size-9" />
        </div>
        <div className="text-3xl font-extrabold tracking-[-0.02em]">
          {pk ? t.pkReadyTitle : t.esTuTurno}
        </div>
        <p className="m-0 mt-2 text-[15px] leading-relaxed opacity-90">
          {pk ? t.pkReadyLead : t.mesaLista}
        </p>
      </div>

      <div className="flex-1 px-5 pb-10 pt-6">
        <Card className="mb-4">
          <CardContent className="flex flex-col gap-3.5 p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground">{pk ? t.pkReservado : t.turno}</div>
                <div className="text-[26px] font-extrabold">
                  <bdi dir="ltr">{party.ticket_no}</bdi>
                </div>
              </div>
              <Badge>{t.llamado}</Badge>
            </div>
            <Separator />
            {!pk && (
              <WaitlistRow
                icon={<Users className="size-[17px]" />}
                label={t.personasLbl}
                value={`${party.party_size}`}
              />
            )}
            <WaitlistRow
              icon={<MapPin className="size-[17px]" />}
              label={t.veA}
              value={pk ? t.pkGoTo : t.recepcion}
            />
            {!pk && party.table_label && (
              <div className="pt-1 text-left text-sm leading-relaxed text-muted-foreground">
                <WaitlistSegments segments={t.teAsignamos(party.table_label, party.party_size)} />
              </div>
            )}
          </CardContent>
        </Card>

        <div
          className="mb-[22px] flex items-center justify-between rounded-lg bg-background-secondary px-4 py-3.5"
          data-testid={arrivalConfirmed ? 'waitlist-arrival-confirmed' : undefined}
          role={arrivalConfirmed ? 'status' : undefined}
          aria-live={arrivalConfirmed ? 'polite' : undefined}
          aria-atomic={arrivalConfirmed ? 'true' : undefined}
        >
          <div className="text-[13px] font-semibold">{arrivalConfirmed ? arrivalLabel : holdLabel}</div>
          {arrivalConfirmed ? (
            <CheckCircle2 className="size-5 text-primary" aria-hidden="true" />
          ) : (
            <span dir="ltr">
              <WaitlistDeadlineCountdown
                expiresAt={party.call_expires_at}
                serverTimeUtc={party.server_time_utc}
                onExpired={onCallExpired}
              />
            </span>
          )}
        </div>

        <div className="flex flex-col gap-2.5">
          {!arrivalConfirmed && errorMessage && <WaitlistWarning>{errorMessage}</WaitlistWarning>}
          {showOrderInApp && (
            <Button
              size="lg"
              variant="outline"
              className="w-full"
              onClick={onOrderInApp}
              data-testid="waitlist-order-in-app"
            >
              {t.pkOrderInApp}
            </Button>
          )}
          {!arrivalConfirmed && (
            <Button
              size="lg"
              className="w-full"
              disabled={arriving || leaving}
              aria-busy={arriving}
              onClick={onArrive}
              data-testid="waitlist-arrive"
            >
              {arriving ? t.gpsChecking : arrivalLabel}
            </Button>
          )}
          {gpsEnabled && !arrivalConfirmed && !arriveError && !arriving && (
            <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
              <MapPin className="size-[13px]" /> {t.gpsNote}
            </div>
          )}
          <Button variant="ghost" className="w-full text-muted-foreground" disabled={arriving || leaving} aria-busy={leaving} onClick={onLeave}>
            {t.noPodre}
          </Button>
        </div>
      </div>
    </div>
  );
}
